import { useConfig } from "./useConfig";
import { useChainInfo } from "./useChainInfo";
import { useNativeToken } from "./useNativeToken";
import { useSnip20Token } from "./useSnip20Token";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { balanceState, balanceStateType } from "../context/balanceState";
import { baseDenom } from "../utils/constants";

export const useToken = () => {
  const config = useConfig();
  const chainInfo = useChainInfo();

  const nativeToken = useNativeToken();
  const snip20Token = useSnip20Token();

  const balanceData = useRecoilValue(balanceState);
  const setBalanceData = useSetRecoilState(balanceState);

  /*
   * Creates the snip20 token viewing key and stores in keplr
   */
  const createViewingKey = async (tokenName: string) => {
    if (config.getAsset(tokenName).native_denom !== null) {
      // native token
      throw new Error(
        `Viewing key not supported for native token ${tokenName}`
      );
    } else {
      // snip20 token
      const keyResponse = await snip20Token.createViewingKey(tokenName);
      return keyResponse;
    }
  };

  const createViewingKeyforLP = async (poolName: string) => {
      // snip20 token
      const { lpAddress, lpCodeHash } = config.getPoolsLPAsset(poolName);
      try {
        const result = await (window as any).keplr?.suggestToken(
          chainInfo.getChainId(),
          lpAddress as string
        );
  
        return result;
      } catch (error) {
        console.debug(error);
      }
      return;
  };

  /*.m
   * Returns token balance (snip20 and native)
   */
  const getBalance = async (tokenName: string, isRefetching?: boolean) => {
    if (
      isRefetching !== true &&
      balanceData[tokenName as keyof balanceStateType] !== "" &&
      balanceData[tokenName as keyof balanceStateType] !== undefined
    ) {
      return balanceData[tokenName as keyof balanceStateType];
    }

    if (config.getAsset(tokenName).native_denom !== null) {
      // native token
      const balResponse = await nativeToken.getBalance(tokenName);
      setBalanceData({
        ...balanceData,
        [tokenName as keyof balanceStateType]: balResponse,
      });
      return balResponse;
    } else {
      let viewingKey;
      try {
        // snip20 token
        viewingKey = await (window as any).keplr?.getSecret20ViewingKey(
          chainInfo.getChainId(),
          config.getAsset(tokenName).contract_addr as string
        );
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === "There is no matched secret20"
        )
          return null;
      }
      const balResponse = await snip20Token.getSnip20Balance(
        tokenName,
        viewingKey
      );
      setBalanceData({
        ...balanceData,
        [tokenName as keyof balanceStateType]: balResponse,
      });
      return balResponse;
    }
  };

  /*
   * Returns token balance (snip20) using token addr (only use for LP token)
   */
  const getBalanceByAddress = async (
    tokenAddr: string,
    tokenCodeHash: string
  ) => {
    let viewingKey;
    try {
      // snip20 token
      viewingKey = await (window as any).keplr?.getSecret20ViewingKey(
        chainInfo.getChainId(),
        tokenAddr as string
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === "There is no matched secret20"
      )
        return null;
    }
    const balance = await snip20Token.getSnip20BalanceByAddress(
      tokenAddr,
      tokenCodeHash,
      viewingKey
    );
    return balance;
  };

  /*
   * Returns token total supply (snip20) using token addr (only use for LP token)
   */
  const getSupplyByAddress = async (
    tokenAddr: string,
    tokenCodeHash: string
  ) => {
    const supply = await snip20Token.getSnip20SupplyByAddress(
      tokenAddr,
      tokenCodeHash
    );
    return supply;
  };

  /*
   * Transfers token to given address using (snip20 and native)
   */
  const doTransfer = async (
    tokenName: string,
    tokenAmount: string,
    destAddress: string,
    gasValue?: string | undefined
  ) => {
    if (config.getAsset(tokenName).native_denom !== null) {
      // native token
      const transferResponse = await nativeToken.doTransfer(
        tokenName,
        tokenAmount,
        destAddress,
        gasValue
      );
      return transferResponse;
    } else {
      // snip20 token
      const transferResponse = await snip20Token.doTransfer(
        tokenName,
        tokenAmount,
        destAddress,
        gasValue
      );
      return transferResponse;
    }
  };

  /*
   * Sends token to given contract (snip20 only)
   */
  const doSend = async (
    tokenName: string,
    tokenAmount: string,
    contractAddress: string,
    contractCodeHash: string,
    msg?: string | undefined,
    gasValue?: string | undefined
  ) => {
    if (tokenName === baseDenom) {
      // native token
      throw new Error(
        `Send transaction not allowed for native token ${tokenName}`
      );
    } else {
      // snip20 token
      const sendResponse = await snip20Token.doSend(
        tokenName,
        tokenAmount,
        contractAddress,
        contractCodeHash,
        msg,
        gasValue
      );
      return sendResponse;
    }
  };

  /*
   * Sends token to given contract (snip20 only)
   */
  const doSendByAddress = async (
    tokenAddress: string,
    tokenCodeHash: string,
    tokenAmount: string,
    contractAddress: string,
    contractCodeHash: string,
    msg?: string | undefined,
    gasValue?: string | undefined
  ) => {
    const sendResponse = await snip20Token.doSendByAddress(
      tokenAddress,
      tokenCodeHash,
      tokenAmount,
      contractAddress,
      contractCodeHash,
      msg,
      gasValue
    );
    return sendResponse;
  };

  /*
   * Increase token allowance for given contract address (snip20 only)
   */
  const doIncreaseAllowance = async (
    tokenName: string,
    tokenAmount: string,
    contractAddress: string,
    gasValue?: string | undefined
  ) => {
    if (config.getAsset(tokenName).native_denom !== null) {
      // native token
      throw new Error(
        `Increase allowance not allowed for native token ${tokenName}`
      );
    } else {
      // snip20 token
      const incAllowanceResponse = await snip20Token.doIncreaseAllowance(
        tokenName,
        tokenAmount,
        contractAddress,
        gasValue
      );
      return incAllowanceResponse;
    }
  };

  return {
    getBalance,
    getBalanceByAddress,
    getSupplyByAddress,
    createViewingKey,
    createViewingKeyforLP,
    // getTokenInfo,
    // getTokenConfig,
    // getAllowance,
    // getTransactionHistory,
    doTransfer,
    doSend,
    doSendByAddress,
    doIncreaseAllowance,
  };
};
